<template>
  <div class="field-wrapper">
    <PeriodSelector
      v-model="period"
      :units="units"
      title="automatedScenarios.fields.duration"
    />
    <FieldErrors
      :key="componentFieldErrorsKey"
      :errors="formValidationErrors"
      field="time_unit_select"
    />
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import PeriodSelector from '@/components/automated-scenarios/fields/PeriodSelector.vue';
import {
  defineComponent,
  PropType, ref, SetupContext, watch,
} from 'vue';
import { asInt } from '@/helpers';
import { convertTimeUnitToSeconds } from '@/helpers/helpers';
import ActionWaitMetadata, {
  ActionWaitData,
} from '@/components/automated-scenarios/metadata/actions/ActionWaitMetadata';
import { Period } from '@/types/automated-scenarios';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { maxBoxWaitDelayForOnSiteAutomation } from '@/composables/workflows/constants';

export default defineComponent({
  name: 'ActionWait',
  components: {
    FieldErrors,
    PeriodSelector,
  },

  props: {
    modelValue: {
      type: Object as PropType<ActionWaitData>,
      required: true,
      default() {
        return ActionWaitMetadata.Create();
      },
    },

    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  emits: ['update:modelValue'],

  setup(props: { modelValue: ActionWaitData; formValidation: any }, context: SetupContext) {
    const { t } = useI18n();
    const formValidationErrors = ref(props.formValidation);
    const componentFieldErrorsKey = ref(0);
    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const units = [
      { value: 'SECOND', label: t('automatedScenarios.fields.durations.second(s)') },
      { value: 'MINUTE', label: t('automatedScenarios.fields.durations.minute(s)') },
      { value: 'HOUR', label: t('automatedScenarios.fields.durations.hour(s)') },
      { value: 'DAY', label: t('automatedScenarios.fields.durations.day(s)') },
      { value: 'MONTH', label: t('automatedScenarios.fields.durations.month(s)') },
    ];

    const period = ref<Period>({
      unit: props.modelValue.time_unit_select.unit,
      value: asInt(props.modelValue.time_unit_select.value),
    });

    const update = () => {
      const currentDelayInSeconds = convertTimeUnitToSeconds({
        value: period.value.value,
        unit: period.value.unit,
      });

      const updateData: ActionWaitData = {
        time_unit_select: {
          value: period.value.value,
          unit: period.value.unit,
        },
      };

      // Only add event and webhook properties if delay is within limits
      if (currentDelayInSeconds <= maxBoxWaitDelayForOnSiteAutomation) {
        updateData.event = 'wait';
        updateData.webhook = props.modelValue.webhook || 0;
      }
      // If delay exceeds limits, event and webhook properties are omitted (undefined)

      context.emit('update:modelValue', ActionWaitMetadata.Create(updateData));
    };

    watch([period], update);

    return {
      formValidationErrors,
      componentFieldErrorsKey,
      units,
      period,
    };
  },
});
</script>
